import MainContainer from '@/components/Shared/MainContainer';
import { PageWrapper } from '@/components/Shared/Wrapper';
import { useTranslation } from 'react-i18next';
import { useGetVoteResultsByShareholder } from '../api/getVotingResultsByShareholder';
import CommonTable from '@/components/Shared/CommonTable';
import { useSelectedCompanyStore } from '@/stores/selectedCompany';
import { voteResultsByShareholderColumns } from '../utils/voteResultsByShareholderColumns';
import type { ColumnsType } from 'antd/es/table';
import { VoteResultByShareholderType } from '../types';
import { Flex } from 'antd';
import { PDFDownloadLink } from '@react-pdf/renderer';
import VotingResultsByShareholderReportPdf from '../components/votingResultsByShareholderReportPdf';
import { StyledButton } from '@/lib/theme/components/Button';
import CloudDownloadOutlined from '@ant-design/icons/CloudDownloadOutlined';
import themeToken from '@lib/theme/tokens/index';
import { useGetCompany } from '@/features/company/api/getCompany';
import { useUserDataStore } from '@/stores/UserData';
import { useParams } from 'react-router-dom';
import VideoIcon from '@/components/Icons/VideoIcon';
import { useGetMeetingDetails } from '@/features/investorRelations/api/meetings/getMeetingDetails';

const VoteResultsByShareholder = () => {
  const { t, i18n } = useTranslation('vote', {
    keyPrefix: 'vote',
  });

  const { meetingID } = useParams<{ meetingID: string }>();

  const { selectedCompany } = useSelectedCompanyStore((state) => state);
  const { userData } = useUserDataStore((state) => state);
  const { data: meetingDetails } = useGetMeetingDetails({
    companyID: selectedCompany?.cid as number,
    meetingID: meetingID ? +meetingID : -1,
  });

  const { data: companyData } = useGetCompany({
    companyID: selectedCompany?.cid as number,
  });

  const currencyCode = companyData?.company_data.company_currency.code ?? '';

  const {
    data: voteResultsByShareholder,
    dataUpdatedAt: voteResultsByShareholderUpdatedAt,
    isPending: isVoteResultsByShareholderLoading,
  } = useGetVoteResultsByShareholder({
    companyID: selectedCompany?.cid as number,
    meetingID: meetingID ? parseInt(meetingID) : undefined,
  });

  let columns: ColumnsType<VoteResultByShareholderType> = [];
  if (voteResultsByShareholder?.data && voteResultsByShareholder.data.length > 0) {
    columns = voteResultsByShareholderColumns({
      isLoading: isVoteResultsByShareholderLoading,
      t,
      data: voteResultsByShareholder.data,
      i18n,
    });
  }

  return (
    <MainContainer
      title={t('voteResultsByShareholder')}
      breadcrumbItems={[
        {
          href: '/investor-relations/meetings',
          title: (
            <>
              <VideoIcon width={12} height={20} active />
              {t('meetings')}
            </>
          ),
        },
        {
          title: t('meetingDetails'),
          href: `/investor-relations/meetings/${meetingID}`,
        },
        { title: t('voteResults') },
      ]}
      headerContent={
        voteResultsByShareholder?.data.length ? (
          <PDFDownloadLink
            key={`${i18n.language}-${voteResultsByShareholderUpdatedAt}`}
            document={
              <VotingResultsByShareholderReportPdf
                companyLogo={companyData?.company_data.logo}
                meetingName={meetingDetails ? meetingDetails?.data.name || '' : ''}
                voteResults={voteResultsByShareholder.data}
                reportHeaderInfo={{
                  company: {
                    currency: currencyCode,
                    name: selectedCompany?.name || '',
                  },
                  user: {
                    email: userData?.userInfo.mail || '',
                  },
                }}
                t={t}
                currentLanguage={i18n.language}
              />
            }
            fileName="voting-results-by-shareholder.pdf"
          >
            <StyledButton
              style={{ padding: `${themeToken.paddingXXS}px ${themeToken.paddingLG}px` }}
              size="small"
              type="primary"
              ghost
              disabled={isVoteResultsByShareholderLoading}
              icon={<CloudDownloadOutlined />}
            >
              {t('download')}
            </StyledButton>
          </PDFDownloadLink>
        ) : (
          <StyledButton
            style={{ padding: `${themeToken.paddingXXS}px ${themeToken.paddingLG}px` }}
            size="small"
            type="primary"
            ghost
            loading={isVoteResultsByShareholderLoading}
            disabled
            icon={<CloudDownloadOutlined />}
          >
            {t('download')}
          </StyledButton>
        )
      }
    >
      <PageWrapper>
        {voteResultsByShareholder?.data && voteResultsByShareholder?.data.length > 0 ? (
          <CommonTable
            dataSource={voteResultsByShareholder.data}
            columns={columns}
            rowKey="id"
            doesHaveFixedColumn={true}
            pagination={false}
            loading={isVoteResultsByShareholderLoading}
            scroll={{ y: 55 * 11, x: 'max-content' }}
          />
        ) : (
          <Flex vertical align="center" justify="center" flex={1}>
            <h2>{t('noResults')}</h2>
          </Flex>
        )}
      </PageWrapper>
    </MainContainer>
  );
};

export default VoteResultsByShareholder;
